/* import __COLOCATED_TEMPLATE__ from './fin-phone-behavior.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import {
  type FinVoiceVoice,
  DEFAULT_VOICE,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: any;
    ruleset: $TSFixMe;
    phoneNumber?: string;
    numberConfiguration?: FinVoiceNumberConfiguration;
    updateFinBehavior: (voice?: FinVoiceVoice, greeting?: string) => Promise<void>;
  };
}

export default class FinPhoneBehavior extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare notificationsService: $TSFixMe;

  @tracked voice?: FinVoiceVoice;
  @tracked greeting = '';

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    this.resetFinBehavior();
  }

  get savingDisabled() {
    return Boolean(
      !this.voice || !this.greeting || !this.args.phoneNumber || this.numberConfiguration?.isSaving,
    );
  }

  get errorMessage() {
    if (!this.args.phoneNumber) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-behavior.no-phone-numbers',
      );
    }

    if (!this.voice) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-behavior.error-no-voice',
      );
    }

    if (!this.greeting) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-behavior.error-no-greeting',
      );
    }

    return undefined;
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get voiceOptions() {
    return this.finVoiceConfigService.voiceOptions;
  }

  get savedVoice() {
    let savedVoice = this.numberConfiguration?.voice;

    if (!savedVoice || !this.voiceOptions.find((voice) => voice.value === savedVoice)) {
      return undefined;
    }

    return savedVoice;
  }

  get savedGreeting() {
    return this.numberConfiguration?.greeting;
  }

  get activeVoiceDescription() {
    if (!this.voice) {
      return '';
    }

    return this.voiceOptions.find((voice) => voice.value === this.voice)?.description;
  }

  get activeVoiceLabel() {
    if (this.savedVoice) {
      return this.voiceOptions.find((voice) => voice.value === this.savedVoice)?.text;
    }

    return '';
  }

  @action
  resetFinBehavior() {
    this.voice = this.savedVoice || DEFAULT_VOICE;
    this.greeting = this.savedGreeting || '';
  }

  @action
  updateVoice(value: FinVoiceVoice) {
    this.voice = value;
  }

  @action
  updateGreeting(event: Event) {
    let target = event.target as HTMLTextAreaElement;
    this.greeting = target.value;
  }

  @action
  async updateFinBehavior() {
    if (!this.voice && !this.greeting) {
      return;
    }

    await this.args.updateFinBehavior(this.voice, this.greeting);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::FinPhoneBehavior': typeof FinPhoneBehavior;
  }
}
